import React, { useCallback, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthHeader, MobileVerificationForm, ModalFactory, ModalLayout } from 'components';
import SignUpSecurity from 'components/SignUp/SignUpSecurity';
import { InviteService } from 'lib';
import validations from 'lib/Validations';
import { routeConstants } from 'routes';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getInviteRequest, selectInvite } from 'store/Invites';
import { completeSignUpRequest, createSignUpUserIdentityRequest, resendIdentityVerificationCodeRequest, selectSignUpUser, SignUp, updateSignUpUserRequest, verifySignUpUserIdentityRequest } from 'store/SignUp';
import { IStore } from 'store/types';
import { getUserInvitesRequest } from 'store/User';
import { ChannelType, IdentityType, IInvite, IUpdateUserRequest } from 'types';
import { formatDateForApi, stripCountryCode } from 'utils';
import { findIdentityByValue } from 'utils/identity';

const SignUpAccountSecurity: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const signUpUser = useSelector(selectSignUpUser());
  const invite = useSelector(selectInvite);
  const mobileIdentityLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.CREATE_SIGN_UP_USER_IDENTITY_REQUEST));
  const updateIdentityLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.UPDATE_SIGN_UP_USER_REQUEST));
  const verifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
  const resendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST));
  const completeLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, SignUp.COMPLETE_SIGN_UP_REQUEST));
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
  const signUpErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, SignUp.CREATE_SIGN_UP_USER_IDENTITY_REQUEST));

  const [hasResentPasscode, setHasResentPasscode] = useState<boolean>(false);

  // Redirect to signup if we don't have a signup user.
  if (!signUpUser) {
    history.replace(routeConstants.public.signUp);
  }

  const validateField = (field: 'identity' | 'recoveryPin' | 'dateOfBirth') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, setFieldValue, getFieldProps, handleBlur, resetForm } = useFormik({
    initialValues: {
      identity: '',
      recoveryPin: '',
      dateOfBirth: '',
    },
    onSubmit: () => {
      if (signUpUser) {
        const payload = {
          ...values,
          identity: stripCountryCode(values.identity),
          dateOfBirth: formatDateForApi(values.dateOfBirth),
        } as IUpdateUserRequest;
        // Create Mobile Identity, then update the Signup User
        dispatch(createSignUpUserIdentityRequest(signUpUser.id, payload, { onSuccess: () => onCreateMobileIdentitySuccess(payload) }));
      }
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape(validations.signUpSecurityValidationSchema),
  });

  const onCreateMobileIdentitySuccess = (payload: IUpdateUserRequest) => {
    dispatch(updateSignUpUserRequest(signUpUser.id, payload, { onSuccess: showVerifyModal }));
  };

  const onNewForm = useCallback(() => {
    dispatch(clearRequest(SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
    dispatch(clearRequest(SignUp.CREATE_SIGN_UP_USER_IDENTITY_REQUEST));
  }, [dispatch]);

  const onVerifySubmit = (verificationCode: string) => {
    const currentMobileIdentity = findIdentityByValue(signUpUser?.identities, IdentityType.MOBILE, values.identity);

    if (currentMobileIdentity && verificationCode) {
      const payload = {
        verifyCode: verificationCode,
      };

      dispatch(verifySignUpUserIdentityRequest(signUpUser.id, currentMobileIdentity.id, payload, { onSuccess: onMobileVerifySuccess }));
    }
  };

  const onMobileVerifySuccess = () => {
    if (signUpUser) {
      dispatch(
        completeSignUpRequest(signUpUser.id, {
          onSuccess: () => onSignUpCompleteSuccess(),
          successMessage: 'Signup successfully completed!',
        })
      );
    }
  };

  const onGetUserInvitesSuccess = (userInvites: IInvite[] | []) => (userInvites?.length ? history.replace(routeConstants.private.rootPath) : history.replace(routeConstants.private.accountSetup.notifications));

  const onSignUpCompleteSuccess = () => {
    // If there is a verified invite, fetch that invite and route to Accept Invites (sign-up with invite link)
    const inviteId = invite?.id || InviteService.getInviteToken();
    if (inviteId !== null) return dispatch(getInviteRequest(inviteId, () => history.replace(routeConstants.private.acceptInvitation)));
    // Otherwise, check if the user has invites and route accordingly (sign-up without invite link)
    return dispatch(getUserInvitesRequest(onGetUserInvitesSuccess));
  };

  const resendSms = () => {
    const currentMobileIdentity = findIdentityByValue(signUpUser?.identities, IdentityType.MOBILE, values.identity);

    if (currentMobileIdentity) {
      dispatch(clearRequest(SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
      dispatch(resendIdentityVerificationCodeRequest(signUpUser?.id, currentMobileIdentity.id, undefined, { successMessage: 'Verification code resent successfully' }));
      setHasResentPasscode(true);
    }
  };

  const resendCall = () => {
    const currentMobileIdentity = findIdentityByValue(signUpUser?.identities, IdentityType.MOBILE, values.identity);

    if (currentMobileIdentity) {
      dispatch(clearRequest(SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST));
      dispatch(resendIdentityVerificationCodeRequest(signUpUser.id, currentMobileIdentity.id, ChannelType.CALL, { successMessage: 'Verification call initiated' }));
    }
  };

  // Modal
  const [showVerifyModal, hideVerifyModal] = useModal(
    () => (
      <ModalFactory
        customModal
        isActive
        onClose={() => {
          hideVerifyModal();
        }}
        closeOnOutSideClick={false}
      >
        <ModalLayout
          isActive
          onClose={() => {
            setHasResentPasscode(false);
            onNewForm();
            hideVerifyModal();
          }}
        >
          <MobileVerificationForm
            mobile={values.identity}
            isLoading={verifyLoading || completeLoading}
            isResendLoading={resendLoading}
            errorMessages={apiErrorsVerify?.errorMessages}
            onSubmit={onVerifySubmit}
            resendVerificationCode={resendSms}
            resendCall={resendCall}
            showCallButton={hasResentPasscode}
          />
        </ModalLayout>
      </ModalFactory>
    ),
    [dispatch, signUpUser, verifyLoading, hasResentPasscode, completeLoading, resendLoading, values.identity, apiErrorsVerify]
  );

  return (
    <SignUpSecurity
      authHeader={
        <AuthHeader
          data-cy="signup-account-security"
          title="Account Security"
          subtitle="Kickfin requires a mobile phone number, date of birth & a pin code to add an extra layer of security to your account.  This information is very important and will be used if you ever need to recover your account."
          onBack={() => {
            resetForm();
            history.goBack();
          }}
        />
      }
      onSubmit={handleSubmit}
      validateField={validateField}
      setFieldValue={setFieldValue}
      getFieldProps={getFieldProps}
      isValid={isValid}
      dirty={dirty}
      values={values}
      errorMessages={signUpErrors?.errorMessages}
      onNewForm={onNewForm}
      formId={SignUp.VERIFY_SIGN_UP_USER_IDENTITY_REQUEST}
      errors={errors}
      isLoading={mobileIdentityLoading || updateIdentityLoading}
      handleBlur={handleBlur}
    />
  );
};

export default SignUpAccountSecurity;
